import { useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { SequenceItem } from '../sequence/sequenceItem'
import { useHall } from '@/contexts/HallProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { SpeakingRole, toMs, TurnType } from '@db8/shared'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useIOSScrollFix } from '@/hooks/useIOSScrollFix'

export const SpeakingSequencePanel = () => {
  const { setHallMetadata, hallMetadata } = useHall()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { hallUsers, isModerator } = useHallUsers()
  const { setSideBarMode, setSecondSideBarMode } = useMeetingAppContext()
  useIOSScrollFix(containerRef)

  const affirmativeTeam = useMemo(
    () => hallUsers.filter((user) => user.speakingRole === SpeakingRole.Affirmative),
    [hallUsers],
  )

  const negativeTeam = useMemo(
    () => hallUsers.filter((user) => user.speakingRole === SpeakingRole.Negative),
    [hallUsers],
  )

  const autoAssignTurns = () => {
    const aff = affirmativeTeam
    const neg = negativeTeam
    let affIdx = 0
    let negIdx = 0

    const nextAff = () => (aff.length ? aff[affIdx++ % aff.length] : null)
    const nextNeg = () => (neg.length ? neg[negIdx++ % neg.length] : null)

    const updatedTurns = hallMetadata?.turns.map((turn) => {
      const updated = { ...turn }

      // Never touch prep turns.
      if (updated.type === TurnType.Preperatory) return updated

      const hasPrimary = !!updated.primarySpeakerId
      const needsSecondary = updated.secondarySpeakerId !== null
      const hasSecondary = needsSecondary ? !!updated.secondarySpeakerId : true

      // Already assigned (including secondary when needed)
      if (hasPrimary && hasSecondary) return updated

      // Normal speeches
      if (updated.type === TurnType.Affirmative && !hasPrimary) {
        const pick = nextAff()
        if (pick) updated.primarySpeakerId = pick.id
        return updated
      }
      if (updated.type === TurnType.Negative && !hasPrimary) {
        const pick = nextNeg()
        if (pick) updated.primarySpeakerId = pick.id
        return updated
      }

      // Directed cross-ex: Slot 1 questions Slot 2
      if (
        updated.type === TurnType.AffirmativeCrossNegative ||
        updated.type === TurnType.NegativeCrossAffirmative
      ) {
        if (updated.type === TurnType.AffirmativeCrossNegative) {
          // AxN => Aff (Q) is primary, Neg (A) is secondary
          if (!updated.primarySpeakerId) updated.primarySpeakerId = nextAff()?.id || updated.primarySpeakerId
          if (!updated.secondarySpeakerId) updated.secondarySpeakerId = nextNeg()?.id || updated.secondarySpeakerId
        } else {
          // NxA => Neg (Q) is primary, Aff (A) is secondary
          if (!updated.primarySpeakerId) updated.primarySpeakerId = nextNeg()?.id || updated.primarySpeakerId
          if (!updated.secondarySpeakerId) updated.secondarySpeakerId = nextAff()?.id || updated.secondarySpeakerId
        }
        return updated
      }

      return updated
    })

    setHallMetadata({ turns: updatedTurns })
  }

  const handleInitialize = () => {
    const nextTurn = hallMetadata?.turns[0]
    const shouldPinSecondary =
      nextTurn?.type === TurnType.AffirmativeCrossNegative ||
      nextTurn?.type === TurnType.NegativeCrossAffirmative

    const pinnedParticipants = shouldPinSecondary
      ? [nextTurn?.primarySpeakerId, nextTurn?.secondarySpeakerId].filter(Boolean)
      : [nextTurn?.primarySpeakerId].filter(Boolean)

    setHallMetadata({
      debateInitialized: true,
      activeTurn: 0,
      timer: nextTurn ? toMs(nextTurn?.duration) : 0,
      pinnedParticipants,
    })

    // Open Speaker Schedule in the left (second) drawer and Transcripts in the right (primary) drawer
    setSecondSideBarMode('SPEAKER SCHEDULE')
    setSideBarMode('TRANSCRIPTS')
  }

  const showInitialize = useMemo(() => {
    // Original behavior: Initialize only appears AFTER all slots are assigned.
    if (hallMetadata.activeTurn !== -1 || !isModerator) return false

    const allTurnsAssigned = hallMetadata?.turns
      .filter((turn) => turn.type !== TurnType.Preperatory)
      .every((turn) => {
        const hasPrimary = !!turn.primarySpeakerId
        const needsSecondary = turn.secondarySpeakerId !== null
        const hasSecondary = needsSecondary ? !!turn.secondarySpeakerId : true
        return hasPrimary && hasSecondary
      })

    return allTurnsAssigned
  }, [hallMetadata.activeTurn, hallMetadata?.turns, isModerator])

  return (
    <div className="w-full h-full flex flex-col items-center justify-between border border-transparent pb-16 text-foreground dark:text-white dark:[&_*]:text-white">
      <div
        ref={containerRef}
        className="w-full h-full relative overflow-y-auto ios-scroll noScrollbar"
      >
        <div className="w-full h-full">
          <div className="w-full p-1">
            {showInitialize && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-full text-sm py-1 border border-green-500 text-green-500 hover:text-green-400 hover:border-green-400"
                      variant="outline"
                      onClick={handleInitialize}
                    >
                      Initialize
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="z-50 p-2 text-xs dark:text-white">
                    <p>
                      When you are ready to start the debate click 'Initialize'.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {hallMetadata.activeTurn === -1 &&
              affirmativeTeam.length > 0 &&
              negativeTeam.length > 0 &&
              !showInitialize &&
              isModerator && (
                <Button
                  onClick={autoAssignTurns}
                  variant="outline"
                  className="w-full mt-auto text-gray-900 border border-gray-900 hover:text-gray-700 hover:border-gray-700 dark:text-white dark:border-white dark:hover:text-gray-200 dark:hover:border-gray-200"
                >
                  Auto Assign
                </Button>
              )}

            <ul className="pb-4 pt-2 space-y-2 text-foreground dark:text-white">
              {hallMetadata?.turns
                ?.filter((turn) => turn.step >= hallMetadata.activeTurn)
                ?.map((turn) => (
                  <SequenceItem key={turn.step} turn={turn} isSelectDisabled={false} />
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
